import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { Upload, CheckCircle, File, X, Loader2 } from 'lucide-react';
import { getCurrentUser } from 'aws-amplify/auth';
import { Button } from '../../ui/button';
import { Progress } from '../../ui/progress';
import { lambdaService } from '../../../services/lambdaService';
import { SignupPrompt } from '../../auth/SignupPrompt';

const ACCEPTED = '.csv,.json,.parquet,.xlsx,.xls,.tsv';
const POLL_INTERVAL = 4000;
const MAX_POLLS = 45; // ~3 minutes

export const UploadNode: React.FC<{ id: string; data: { label: string; color: string; demoState?: { status: string; fileName?: string; progress?: number } } }> = ({ id, data }) => {
  const { updateNodeData } = useReactFlow();
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<'idle' | 'uploading' | 'processing' | 'done' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [showSignup, setShowSignup] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    getCurrentUser().catch(() => setIsAuthenticated(false));
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Demo mode: override internal state with injected demoState
  const demo = data.demoState;
  const displayStatus = demo?.status || status;
  const displayFileName = demo?.fileName || file?.name || '';
  const displayProgress = demo?.progress ?? progress;

  const pollForProcessedFile = useCallback(
    (uploadedFileName: string) => {
      const baseName = uploadedFileName.replace(/\.[^.]+$/, '').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_.-]/g, '').toLowerCase();
      let pollCount = 0;
      let datasetFolder: string | null = null;

      pollRef.current = setInterval(async () => {
        pollCount++;
        if (pollCount > MAX_POLLS) {
          if (pollRef.current) clearInterval(pollRef.current);
          setStatus('done');
          return;
        }
        try {
          const [filesRes, statusRes] = await Promise.all([
            lambdaService.listFiles(),
            lambdaService.getProcessingStatus(),
          ]);

          // Extract dataset_folder from processing status for better matching
          if (!datasetFolder && statusRes.success && statusRes.processing_files.length > 0) {
            const processingMatch = statusRes.processing_files.find((pf) => {
              const origName = (pf.original_filename || '').replace(/\.[^.]+$/, '').toLowerCase();
              const pfName = (pf.filename || '').replace(/\.[^.]+$/, '').replace(/^\d+-/, '').toLowerCase();
              return origName === baseName || pfName === baseName ||
                     origName.startsWith(baseName) || pfName.startsWith(baseName);
            });
            if (processingMatch?.chunk_info?.dataset_folder) {
              datasetFolder = processingMatch.chunk_info.dataset_folder;
            }
          }

          // Search processed files list
          if (filesRes.success && filesRes.files) {
            const match = filesRes.files.find((f) => {
              // Match by dataset folder (most reliable)
              if (datasetFolder) {
                if (f.folderPath && f.folderPath.includes(datasetFolder)) return true;
                if (f.s3Key && f.s3Key.includes(datasetFolder)) return true;
              }
              // Match by originalFilename
              if (f.originalFilename) {
                const origName = f.originalFilename.replace(/\.[^.]+$/, '').replace(/^\d+-/, '').toLowerCase();
                if (origName === baseName || origName.startsWith(baseName)) return true;
              }
              // Match by displayName (existing logic)
              const name = f.fileName.replace(/^\d+-/, '').toLowerCase();
              return name === baseName || name.startsWith(baseName);
            });
            if (match) {
              if (pollRef.current) clearInterval(pollRef.current);
              updateNodeData(id, { selectedFile: match });
              setStatus('done');
            }
          }
        } catch {
          // ignore poll errors, keep trying
        }
      }, POLL_INTERVAL);
    },
    [id, updateNodeData]
  );

  const handleFile = useCallback(
    async (f: File) => {
      if (!isAuthenticated) { setShowSignup(true); return; }
      setFile(f);
      setStatus('uploading');
      setProgress(0);
      setErrorMsg('');
      try {
        const res = await lambdaService.uploadFile(f, (p) => setProgress(p));
        if (res.success && res.s3Key) {
          setProgress(100);
          setStatus('processing');
          window.dispatchEvent(new CustomEvent('bigsplice:file-uploaded'));
          pollForProcessedFile(res.fileName || f.name);
        } else {
          throw new Error(res.error || 'Upload failed');
        }
      } catch (e: any) {
        setStatus('error');
        setErrorMsg(e.message || 'Upload failed');
      }
    },
    [isAuthenticated, pollForProcessedFile]
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const f = e.dataTransfer.files[0];
      if (f) handleFile(f);
    },
    [handleFile]
  );

  const handleClick = useCallback(() => {
    if (!isAuthenticated) { setShowSignup(true); return; }
    inputRef.current?.click();
  }, [isAuthenticated]);

  const reset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setFile(null);
    setProgress(0);
    setStatus('idle');
    setErrorMsg('');
    updateNodeData(id, { selectedFile: undefined });
  };

  return (
    <div className="bg-card border rounded-xl shadow-md w-[300px] overflow-hidden">
      <div
        className="px-3 py-2 text-xs font-semibold text-white flex items-center gap-1.5"
        style={{ background: data.color }}
      >
        <Upload className="h-3.5 w-3.5" />
        {data.label}
      </div>

      <div className="p-3">
        {displayStatus === 'idle' && (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={onDrop}
            className={`nodrag border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
              isDragOver
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-primary/50'
            }`}
            onClick={handleClick}
          >
            <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">
              Drag & drop or <span className="text-primary font-medium">choose file</span>
            </p>
            <p className="text-[10px] text-muted-foreground mt-1">CSV, Excel, JSON, Parquet, TSV</p>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED}
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
          </div>
        )}

        {displayStatus === 'uploading' && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <File className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-xs truncate">{displayFileName}</span>
            </div>
            <Progress value={displayProgress} className="h-1.5" />
            <p className="text-[10px] text-muted-foreground text-right">{Math.round(displayProgress)}%</p>
          </div>
        )}

        {displayStatus === 'processing' && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 text-blue-500 shrink-0 animate-spin" />
              <span className="text-xs truncate flex-1">{displayFileName}</span>
            </div>
            <p className="text-[10px] text-muted-foreground">Processing...</p>
          </div>
        )}

        {displayStatus === 'done' && (
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
            <span className="text-xs truncate flex-1">{displayFileName}</span>
            {!demo && (
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={reset}>
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        )}

        {displayStatus === 'error' && (
          <div className="space-y-1">
            <p className="text-xs text-destructive">{errorMsg}</p>
            <Button variant="outline" size="sm" className="w-full text-xs h-7" onClick={reset}>
              Try again
            </Button>
          </div>
        )}
      </div>

      <Handle type="source" position={Position.Right} className="!bg-muted-foreground" />
      <SignupPrompt open={showSignup} onOpenChange={setShowSignup} />
    </div>
  );
};
